import React from 'react';
import styled from 'styled-components';

/**
 * Lightweight, dependency-free chart for Goal-Agent evidence: horizontal bars derived directly
 * from a sub-answer's (columnNames, results). No LLM/chart-service call — fast + reliable.
 * Picks the (last) numeric column as the value and the remaining column(s) as the label.
 */
const Wrap = styled.div`display: flex; flex-direction: column; gap: 5px; margin: 8px 0;`;
const Row = styled.div`display: flex; align-items: center; gap: 8px; font-size: 11.5px; direction: rtl;`;
const Label = styled.div`
  flex: 0 0 ${p => p.$w || 120}px; text-align: right; color: #475569;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const Track = styled.div`flex: 1; height: 14px; background: #f1f5f9; border-radius: 4px; overflow: hidden;`;
const Bar = styled.div`height: 100%; background: ${p => p.$color}; width: ${p => p.$pct}%; border-radius: 4px; transition: width .3s ease;`;
const Val = styled.div`flex: 0 0 auto; color: #0f172a; font-weight: 600; font-variant-numeric: tabular-nums; min-width: 40px; text-align: left;`;

function extract(columnNames, results) {
  if (!columnNames || !results || !results.length) return null;
  const isNum = (v) => typeof v === 'number' || (v !== null && v !== '' && !isNaN(parseFloat(v)) && isFinite(v));
  const idxs = columnNames.map((_, i) => i);
  const numIdx = idxs.filter((i) => results.every((r) => isNum(r[i])));
  if (!numIdx.length) return null;
  const valIdx = numIdx[numIdx.length - 1];                 // value = the (last) numeric column
  const labelIdxs = idxs.filter((i) => i !== valIdx);        // label = everything else
  if (!labelIdxs.length) return null;
  return results
    .map((r) => ({ label: labelIdxs.map((i) => r[i]).filter((x) => x != null).join(' '), value: Number(r[valIdx]) }))
    .filter((d) => d.label && isFinite(d.value));
}

export default function GoalChart({ columnNames, results, color = '#1B8C3C', big = false }) {
  const data = extract(columnNames, results);
  if (!data || !data.length) return null;
  const rows = data.slice(0, big ? 12 : 6);
  const maxVal = Math.max(...rows.map((d) => d.value), 1);
  return (
    <Wrap>
      {rows.map((d, i) => (
        <Row key={i}>
          <Label $w={big ? 140 : 115} title={d.label}>{d.label}</Label>
          <Track><Bar $pct={Math.max(2, (d.value / maxVal) * 100)} $color={color} /></Track>
          <Val>{d.value.toLocaleString()}</Val>
        </Row>
      ))}
    </Wrap>
  );
}

// True if the data can be charted (used to choose a headline sub-answer)
export function isChartable(columnNames, results) {
  const d = extract(columnNames, results);
  return !!(d && d.length);
}
